import type {
    LookupCategory,
    LookupDimensionGroup,
    LookupItem,
} from '../AskAsperAgentic.types';
import { scoreItem, type MatchRange } from './lookupSearchAlgo';

/**
 * Chart palette used by EnhancedBarChartV2 (and sibling chart components).
 * Entity-type tags use the same colour at each index as COLUMN_ORDER below.
 */
export const CHART_DIMENSION_COLORS = [
    '#F38181', // Time Period — soft pink (matches portfolio filter mock)
    '#AA96DA', // Manufacturer
    '#5B50D1', // Category
    '#A8D8EA', // Brand Family
    '#5B9BD5', // Brand
    '#95E1D3', // Pack Format
    '#DBC7A4', // Price Segment
    '#4ECDC4', // Product Type
    '#7DD3E8', // Product Segment
    '#FFD93D', // overflow / unknown
] as const;

/** Preferred display order for product-level / entity-type buckets. */
const COLUMN_ORDER = [
    'time period',
    'manufacturer',
    'category',
    'brand family',
    'brand',
    'pack format',
    'price segment',
    'product type',
    'product segment',
];

/** Static Time Period values surfaced as the first slash-command bucket. */
export const DEFAULT_TIME_PERIOD_ITEMS: LookupItem[] = [
    { column_name: 'Time Period', entity_name: '4 Weeks' },
    { column_name: 'Time Period', entity_name: '12 Weeks' },
    { column_name: 'Time Period', entity_name: '26 Weeks' },
    { column_name: 'Time Period', entity_name: '52 Weeks' },
    { column_name: 'Time Period', entity_name: 'YTD' },
];

const TIME_PERIOD_CATEGORY: LookupCategory = {
    id: 'time period',
    label: 'Time Period',
    items: DEFAULT_TIME_PERIOD_ITEMS,
};

function hexToRgba(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function normalizeColumnKey(columnName: string): string {
    const normalized = columnName.trim().toLowerCase().replace(/_/g, ' ');
    if (normalized === 'time') return 'time period';
    return normalized;
}

/** Human-readable label, e.g. `brand` → `Brand`, `brand family` → `Brand Family`. */
export function formatColumnLabel(columnName: string): string {
    return columnName
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

/** Uppercase hyphenated tag, e.g. `Brand Family` → `BRAND-FAMILY`. */
export function formatColumnTag(columnName: string): string {
    return formatColumnLabel(columnName).toUpperCase().replace(/\s+/g, '-');
}

/** Stable palette index for a column name (falls back to hash-ish cycle for unknowns). */
export function getColumnPaletteIndex(columnName: string): number {
    const key = normalizeColumnKey(columnName);
    const idx = COLUMN_ORDER.indexOf(key);
    if (idx >= 0) return idx;
    // Stable fallback for unknown dimensions so colour doesn't jump with list order.
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    return hash % CHART_DIMENSION_COLORS.length;
}

/** Chart series colour from palette index (cycles through full chart palette). */
export function getPaletteColor(paletteIndex: number): string {
    const len = CHART_DIMENSION_COLORS.length;
    const idx = ((paletteIndex % len) + len) % len;
    return CHART_DIMENSION_COLORS[idx];
}

/** @deprecated Prefer `getColumnTagStyleForName` / `getPaletteColor` for stable colouring. */
export function getColumnChartColor(columnName: string, paletteIndex?: number): string {
    if (paletteIndex !== undefined) return getPaletteColor(paletteIndex);
    return getPaletteColor(getColumnPaletteIndex(columnName));
}

export function getColumnTagStyle(paletteIndex: number): { bg: string; text: string } {
    const text = getPaletteColor(paletteIndex);
    return { bg: hexToRgba(text, 0.16), text };
}

/** Stable tag style for an entity type / column name. */
export function getColumnTagStyleForName(columnName: string): { bg: string; text: string } {
    return getColumnTagStyle(getColumnPaletteIndex(columnName));
}

export function sortColumnNames(names: string[]): string[] {
    const orderIndex = (name: string) => {
        const idx = COLUMN_ORDER.indexOf(normalizeColumnKey(name));
        return idx === -1 ? COLUMN_ORDER.length : idx;
    };
    return [...names].sort((a, b) => {
        const orderDiff = orderIndex(a) - orderIndex(b);
        if (orderDiff !== 0) return orderDiff;
        return formatColumnLabel(a).localeCompare(formatColumnLabel(b));
    });
}

/** Group runtime lookup items into product-level categories (one per distinct `column_name`). */
export function deriveLookupCategoriesFromItems(items: LookupItem[]): LookupCategory[] {
    if (!items || items.length === 0) return [];

    const byColumn = new Map<string, LookupItem[]>();
    for (const item of items) {
        const key = item.column_name;
        const bucket = byColumn.get(key);
        if (bucket) {
            bucket.push(item);
        } else {
            byColumn.set(key, [item]);
        }
    }

    return sortColumnNames([...byColumn.keys()])
        .filter((columnName) => normalizeColumnKey(columnName) !== 'time period')
        .map((columnName) => ({
            id: normalizeColumnKey(columnName),
            label: formatColumnLabel(columnName),
            items: byColumn.get(columnName)!,
        }));
}

/** Build the slash-command category list with Time Period pinned first. */
export function buildLookupCategories(items: LookupItem[]): LookupCategory[] {
    return [TIME_PERIOD_CATEGORY, ...deriveLookupCategoriesFromItems(items)];
}

/** Stable category id for a host dimension group row (Product / Market). */
export function dimensionCategoryId(groupId: string): string {
    return `dimension:${normalizeColumnKey(groupId)}`;
}

/** True when the category id refers to a dimension-group drill-in row. */
export function isDimensionCategoryId(categoryId: string): boolean {
    return categoryId.startsWith('dimension:');
}

/** Normalized column keys listed across all dimension groups (+ Time Period). */
export function configuredEntityTypeKeys(
    groups: LookupDimensionGroup[],
): Set<string> {
    const keys = new Set<string>(['time period']);
    for (const group of groups) {
        for (const entityType of group.entityTypes) {
            keys.add(normalizeColumnKey(entityType));
        }
    }
    return keys;
}

/**
 * Bare `/` rows when the host passes dimension groups:
 * Time Period, then each group in config array order.
 */
export function buildDimensionTopLevelCategories(
    groups: LookupDimensionGroup[],
): LookupCategory[] {
    return [
        TIME_PERIOD_CATEGORY,
        ...groups.map((group) => ({
            id: dimensionCategoryId(group.id),
            label: group.label,
            dimensionGroupId: group.id,
        })),
    ];
}

/**
 * Entity-type categories for one dimension group, in `entityTypes` config order.
 * Unmapped / empty types still appear so the host order is visible; items are
 * taken from runtime lookupItems when the column matches.
 */
export function buildCategoriesForDimensionGroup(
    group: LookupDimensionGroup,
    items: LookupItem[],
): LookupCategory[] {
    const byKey = new Map<string, LookupItem[]>();
    for (const item of items) {
        const key = normalizeColumnKey(item.column_name);
        const bucket = byKey.get(key);
        if (bucket) bucket.push(item);
        else byKey.set(key, [item]);
    }

    return group.entityTypes.map((entityType) => {
        const key = normalizeColumnKey(entityType);
        return {
            id: key,
            // Preserve host-provided spelling (ProductClass) rather than title-casing.
            label: entityType.trim(),
            items: byKey.get(key) ?? [],
        };
    });
}

/**
 * All configured entity-type categories across groups (config order), excluding
 * Time Period — used for top-level typed search of nested type labels.
 */
export function buildConfiguredEntityCategories(
    groups: LookupDimensionGroup[],
    items: LookupItem[],
): LookupCategory[] {
    const seen = new Set<string>();
    const out: LookupCategory[] = [];
    for (const group of groups) {
        for (const category of buildCategoriesForDimensionGroup(group, items)) {
            if (seen.has(category.id)) continue;
            seen.add(category.id);
            out.push(category);
        }
    }
    return out;
}

/** Filter lookup items to columns allowed by the dimension config (+ Time Period). */
export function filterItemsToConfiguredTypes(
    items: LookupItem[],
    groups: LookupDimensionGroup[],
): LookupItem[] {
    const allowed = configuredEntityTypeKeys(groups);
    return items.filter((item) => allowed.has(normalizeColumnKey(item.column_name)));
}

export interface ScoredCategory {
    category: LookupCategory;
    score: number;
    ranges: MatchRange[];
}

/**
 * Score entity-type labels with the same match family as values (exact / prefix /
 * substring / fuzzy), plus hyphen / whitespace tolerance (`packformat` ↔ Pack Format).
 */
export function scoreCategory(
    query: string,
    category: LookupCategory,
): ScoredCategory | null {
    const q = query.trim();
    if (!q) {
        return { category, score: 0, ranges: [] };
    }

    const candidates = [category.label, formatColumnTag(category.label).replace(/-/g, ' ')];
    let best: { score: number; ranges: MatchRange[] } | null = null;

    for (const candidate of candidates) {
        const scored = scoreItem(q, candidate);
        if (scored && (!best || scored.score > best.score)) {
            best = scored;
        }
    }

    if (!best) return null;
    // Highlight ranges are relative to `category.label` when that candidate won;
    // if only the tag variant matched, drop ranges rather than mis-highlight.
    const labelScored = scoreItem(q, category.label);
    return {
        category,
        score: best.score,
        ranges: labelScored?.ranges ?? [],
    };
}

export function filterCategoriesByQuery(
    categories: LookupCategory[],
    query: string,
): ScoredCategory[] {
    const q = query.trim();
    if (!q) {
        return categories.map((category) => ({ category, score: 0, ranges: [] }));
    }

    const matches: ScoredCategory[] = [];
    for (const category of categories) {
        const scored = scoreCategory(q, category);
        if (scored) matches.push(scored);
    }

    matches.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        // Preserve hierarchy order as a stable tie-breaker.
        return (
            getColumnPaletteIndex(a.category.label) - getColumnPaletteIndex(b.category.label)
        );
    });
    return matches;
}

/** @deprecated Prefer `filterCategoriesByQuery` which returns ranked matches. */
export function categoryMatchesQuery(category: LookupCategory, query: string): boolean {
    if (!query) return true;
    return scoreCategory(query, category) != null;
}
